import React, { useContext, useEffect, useRef, useState } from "react";
import { SdfButton, SdfSelect } from "@waypoint/react-components";

import { AssistContext, maxMessageLength } from "./AssistProvider";
import Toast from "../../common/toast";
import "./Assist.scss";

/**
 * @param {{ className?: string, isModalText?: boolean }} props
 */
const TextArea = ({ className, isModalText }) => {
  const {
    addUserMessage, sessionId, modalOpen, messages, wait, isAdmin,
    selectedProduct, setSelectedProduct, products, productsError
  } = useContext(AssistContext);
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState("");
  const textRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!isModalText) return;
    if (!modalOpen) return;
    if (focused) return;
    if (!sessionId) return;
    
    const handleGlobalKeyDown = (event) => {
      if (document.activeElement !== textRef.current) {
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          textRef.current?.focus();
          setValue((lastValue) => lastValue + (event.shiftKey ? event.key.toUpperCase() : event.key));
          setFocused(true);
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [sessionId, focused, isModalText, modalOpen]);

  useEffect(() => {
    if (!isModalText) return;
    if (!modalOpen) return;
    if (!sessionId) return;

    if (focused) {
      const handleBlur = () => setFocused(false);
      textRef.current.addEventListener('blur', handleBlur);

      return () => {
        textRef.current.removeEventListener('blur', handleBlur);
      };
    } else {
      const handleFocus = () => setFocused(true);
      textRef.current.addEventListener('focus', handleFocus);

      return () => {
        textRef.current.removeEventListener('focus', handleFocus);
      };
    }
  }, [sessionId, focused, isModalText, modalOpen]);

  const fullClassName = `TextArea flex gap-2 w-full bg-default border-solid border-2 rounded-lg hover:border-focus${
    className ? ` ${className}` : ""
  }`;

  let disabled;
  let disabledMessage;

  if (!sessionId) {
    disabled = true;
  } else if (value.length === 0) {
    disabled = true;
  } else if (!!messages.find((message) => message.status === "pending")) {
    disabled = true;
    disabledMessage = "Please wait for the assistance response before sending another message.";
  } else if (wait > 0) {
    disabled = true;
    disabledMessage = `Please wait ${wait} seconds before sending another message.`;
  } else if (value.length > maxMessageLength) {
    disabled = true;
    disabledMessage = `Please reduce your message from ${value.length} to ${maxMessageLength} characters.`;
  }

  const onChange = (e) => {
    setValue(e.target.value);
  };

  const onClick = (e) => {
    e.preventDefault();
    textRef.current?.blur();
    buttonRef.current?.blur();
    addUserMessage(value);
    setFocused(false);
    setValue("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !disabled) {
      onClick(e);
    }
  };

  return (
    <div className="flex flex-col gap-0">
      <div className={fullClassName}>
        <textarea
          disabled={!sessionId}
          ref={textRef}
          value={value}
          className="flex-grow ml-2 min-w-0 text-body-01 font-default"
          {...{ onChange, onKeyDown }}
        />
        <div className="mt-auto mb-2 mr-2">
          {disabled ? // this is a problem with SdfButton
            <SdfButton
            disabled
              ref={buttonRef}
              iconOnly
              buttonTitle="Submit"
              icon="nav-move-next"
              size="sm"
              emphasis="primary"
              {...{ onClick }}
            /> :
            <SdfButton
              ref={buttonRef}
              iconOnly
              buttonTitle="Submit"
              icon="nav-move-next"
              size="sm"
              emphasis="primary"
              {...{ onClick }}
            />
          }
        </div>
      </div>
      {isAdmin && (
        <div className="AssistProductDropdown pt-2">
          <SdfSelect
            className={"w-1/4 mb-2"}
            label={"Select product"}
            items={products}
            input_width={"sm"}
            value={selectedProduct}
            onSdfChange={({ detail }) => {
              setSelectedProduct(detail);
            }}
          />
          {productsError && (
            <Toast
              key={`AssistTextArea-${productsError.message}`}
              status={productsError.status}
              message={productsError.message}
              showToast={"true"}
            />
          )}
        </div>
      )}
      <div className="TextAreaWarning pl-2 text-body-01 text-alert-error">
          {disabledMessage ? `* ${disabledMessage}` : ""}
      </div>
    </div>
  );
};

export default TextArea;